"use client";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FC, PropsWithChildren, useMemo, useState } from "react";
import { z } from "zod";
import { BlogCard } from "./blog-card";
import { Button } from "./button";
import { Pagination, PaginationContent, PaginationItem } from "./pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { postSchema } from "@/renderer/schema";

interface BlogListProps {
  tags: string[];
  posts: z.infer<typeof postSchema>[];
}

interface FilterButtonProps {
  selected: boolean;
  onClick: () => void;
}

const FilterButton: React.FC<PropsWithChildren<FilterButtonProps>> = ({
  children,
  selected,
  onClick,
}) => {
  return (
    <Button
      onClick={onClick}
      variant={selected ? "default" : "secondary"}
      className={selected ? "" : "border border-[#5263ff]"}
    >
      {children}
    </Button>
  );
};

const BLOG_COUNT = 9;

const BlogList: FC<BlogListProps> = ({ tags, posts }) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string>("new");

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      if (selectedTags.length === 0) return true;
      return post.tag ? selectedTags.includes(post.tag) : false;
    });
  }, [posts, selectedTags]);

  const paginatedPosts = useMemo(() => {
    return filteredPosts.slice((page - 1) * BLOG_COUNT, page * BLOG_COUNT);
  }, [filteredPosts, page]);

  const sortedPosts = useMemo(() => {
    return paginatedPosts.sort((a, b) => {
      if (sort === "new") {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      } else {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });
  }, [paginatedPosts, sort]);

  const pageCount = useMemo(() => {
    return Math.ceil(filteredPosts.length / BLOG_COUNT);
  }, [filteredPosts]);

  return (
    <>
      <div className="flex px-4 md:px-0 gap-4 flex-col md:flex-row justify-between">
        <div className="flex gap-4 md:px-0 overflow-y-scroll">
          <FilterButton
            selected={selectedTags.length === 0}
            onClick={() => setSelectedTags([])}
          >
            All
          </FilterButton>
          {tags.map((tag) => {
            const isSelected = selectedTags.includes(tag);
            return (
              <FilterButton
                selected={isSelected}
                onClick={() => {
                  setSelectedTags((prev) =>
                    isSelected
                      ? prev.filter((t) => t !== tag)
                      : [...prev, tag].sort()
                  );
                }}
                key={tag}
              >
                {tag}
              </FilterButton>
            );
          })}
        </div>
        <div className="z-20">
          <Select value={sort} onValueChange={(value) => setSort(value)}>
            <SelectTrigger className="rounded-3xl text-gray-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new" className="text-gray-600">
                Newest First
              </SelectItem>
              <SelectItem value="old" className="text-gray-600">
                Oldest First
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 gap-y-12 px-4 md:px-0">
        {sortedPosts?.map((post) => (
          <BlogCard key={post.id} post={post} />
        ))}
      </div>
      <Pagination>
        <PaginationContent>
          <PaginationItem
            className="flex items-center rounded-full bg-white h-[35px] w-[35px] justify-center cursor-pointer hover:bg-[#5263ff] hover:text-white"
            onClick={() => setPage((p) => (p - 1 > 1 ? p - 1 : 1))}
          >
            <ChevronLeft className="h-5 w-5" />
          </PaginationItem>
          {Array.from({ length: pageCount }).map((_, i) => {
            const pageNumber = i + 1;
            return (
              <PaginationItem
                key={pageNumber}
                className={cn(
                  "flex items-center cursor-pointer hover:bg-[#5263ff] hover:text-white rounded-full bg-white h-[35px] w-[35px] justify-center",
                  pageNumber === page ? "text-white bg-[#5263ff]" : ""
                )}
                onClick={() => setPage(pageNumber)}
              >
                <span>{pageNumber}</span>
              </PaginationItem>
            );
          })}
          <PaginationItem
            className="flex items-center rounded-full bg-white h-[35px] w-[35px] justify-center cursor-pointer hover:bg-[#5263ff] hover:text-white"
            onClick={() =>
              setPage((p) => (p + 1 <= pageCount ? p + 1 : pageCount))
            }
          >
            <ChevronRight className="h-5 w-5" />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </>
  );
};

BlogList.displayName = "BlogList";

export default BlogList;
